import { useQuery, QueryClient } from '@tanstack/react-query'
import axiosInstance from 'src/utils/axios'
import { getPeriodRange, getPeriodGranularity } from 'src/pages/dashboard/reports/period-presets'

export interface ReportFilters {
  startDate?: string
  endDate?: string
  seller?: string
  tags?: string[]
  columns?: string[]
  lossReason?: string
}

type ExtraParams = { type?: string; granularity?: 'day' | 'month' }

function buildParams(filters: ReportFilters & ExtraParams) {
  const params: Record<string, string> = {}
  if (filters.startDate) params.startDate = filters.startDate
  if (filters.endDate) params.endDate = filters.endDate
  if (filters.seller) params.seller = filters.seller
  if (filters.tags && filters.tags.length > 0) params.tags = filters.tags.join(',')
  if (filters.columns && filters.columns.length > 0) params.columns = filters.columns.join(',')
  if (filters.lossReason) params.lossReason = filters.lossReason
  if (filters.type) params.type = filters.type
  if (filters.granularity) params.granularity = filters.granularity
  return params
}

async function fetchReport(endpoint: string, filters: ReportFilters & ExtraParams) {
  const res = await axiosInstance.get(`/reports/${endpoint}`, { params: buildParams(filters) })
  return res.data
}

// ── Cache estratégico ──
// staleTime: dados considerados "frescos" por 2 min → ao voltar para a página
// nesse intervalo, mostra direto do cache (zero loading).
// keepPreviousData: ao trocar filtro, mantém o gráfico anterior visível enquanto
// busca o novo (sem flicker de "carregando").
const REPORT_QUERY_OPTIONS = {
  staleTime: 2 * 60_000,
  cacheTime: 10 * 60_000,
  keepPreviousData: true,
  refetchOnMount: false,
  refetchOnWindowFocus: true,
} as const

type HookOptions = { enabled?: boolean }

function buildOptions(opts?: HookOptions) {
  return {
    ...REPORT_QUERY_OPTIONS,
    enabled: opts?.enabled ?? true,
  }
}

export function useReportSummary(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'summary', filters], () => fetchReport('summary', filters), buildOptions(opts))
}

export function useReportBySeller(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'by-seller', filters], () => fetchReport('by-seller', filters), buildOptions(opts))
}

export function useReportByDDD(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'by-ddd', filters], () => fetchReport('by-ddd', filters), buildOptions(opts))
}

export function useReportByDDI(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'by-ddi', filters], () => fetchReport('by-ddi', filters), buildOptions(opts))
}

export function useReportByState(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'by-state', filters], () => fetchReport('by-state', filters), buildOptions(opts))
}

export function useReportByCountry(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'by-country', filters], () => fetchReport('by-country', filters), buildOptions(opts))
}

export function useReportByCity(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'by-city', filters], () => fetchReport('by-city', filters), buildOptions(opts))
}

export function useReportByPeriod(
  filters: ReportFilters & { granularity?: 'day' | 'month' },
  opts?: HookOptions,
) {
  return useQuery(
    ['reports', 'by-period', filters],
    () => fetchReport('by-period', filters),
    buildOptions(opts),
  )
}

export function useReportByItems(
  filters: ReportFilters & { type: string },
  opts?: HookOptions,
) {
  return useQuery(
    ['reports', 'by-items', filters],
    () => fetchReport('by-items', filters),
    buildOptions(opts),
  )
}

export function useReportKanbanFunnel(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(
    ['reports', 'kanban-funnel', filters],
    () => fetchReport('kanban-funnel', filters),
    buildOptions(opts),
  )
}

export function useReportByTag(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(['reports', 'by-tag', filters], () => fetchReport('by-tag', filters), buildOptions(opts))
}

export function useReportByLossReason(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(
    ['reports', 'by-loss-reason', filters],
    () => fetchReport('by-loss-reason', filters),
    buildOptions(opts),
  )
}

export function useReportHostingSummary(filters: ReportFilters, opts?: HookOptions) {
  return useQuery(
    ['reports', 'hosting-summary', filters],
    () => fetchReport('hosting-summary', filters),
    buildOptions(opts),
  )
}

// ──────────────────────────────────────────
// PREFETCH — chamado logo após autenticação para deixar a página de
// "Relatórios" praticamente instantânea ao abrir.
// ──────────────────────────────────────────
export function prefetchReports(queryClient: QueryClient, opts?: { isMaster?: boolean }) {
  const isMaster = opts?.isMaster ?? false
  const { startDate, endDate } = getPeriodRange('currentMonth', '', '')
  const granularity = getPeriodGranularity(startDate, endDate)
  const baseFilters: ReportFilters = { startDate, endDate }

  const PREFETCH_OPTIONS = { staleTime: 2 * 60_000 }

  // Comuns a todos os usuários (vendedor comum recebe escopado pelo backend).
  const tasks: Array<Promise<unknown>> = [
    queryClient.prefetchQuery(
      ['reports', 'summary', baseFilters],
      () => fetchReport('summary', baseFilters),
      PREFETCH_OPTIONS,
    ),
    queryClient.prefetchQuery(
      ['reports', 'by-seller', baseFilters],
      () => fetchReport('by-seller', baseFilters),
      PREFETCH_OPTIONS,
    ),
    queryClient.prefetchQuery(
      ['reports', 'by-period', { ...baseFilters, granularity }],
      () => fetchReport('by-period', { ...baseFilters, granularity }),
      PREFETCH_OPTIONS,
    ),
    queryClient.prefetchQuery(
      ['reports', 'kanban-funnel', baseFilters],
      () => fetchReport('kanban-funnel', baseFilters),
      PREFETCH_OPTIONS,
    ),
    queryClient.prefetchQuery(
      ['reports', 'by-loss-reason', baseFilters],
      () => fetchReport('by-loss-reason', baseFilters),
      PREFETCH_OPTIONS,
    ),
    queryClient.prefetchQuery(
      ['reports', 'hosting-summary', baseFilters],
      () => fetchReport('hosting-summary', baseFilters),
      PREFETCH_OPTIONS,
    ),
  ]

  // Endpoints exclusivos do master.
  if (isMaster) {
    tasks.push(
      queryClient.prefetchQuery(
        ['reports', 'by-items', { ...baseFilters, type: 'todos' }],
        () => fetchReport('by-items', { ...baseFilters, type: 'todos' }),
        PREFETCH_OPTIONS,
      ),
      queryClient.prefetchQuery(
        ['reports', 'by-tag', baseFilters],
        () => fetchReport('by-tag', baseFilters),
        PREFETCH_OPTIONS,
      ),
      queryClient.prefetchQuery(
        ['reports', 'by-ddd', baseFilters],
        () => fetchReport('by-ddd', baseFilters),
        PREFETCH_OPTIONS,
      ),
      queryClient.prefetchQuery(
        ['reports', 'by-ddi', baseFilters],
        () => fetchReport('by-ddi', baseFilters),
        PREFETCH_OPTIONS,
      ),
      queryClient.prefetchQuery(
        ['reports', 'by-state', baseFilters],
        () => fetchReport('by-state', baseFilters),
        PREFETCH_OPTIONS,
      ),
      queryClient.prefetchQuery(
        ['reports', 'by-country', baseFilters],
        () => fetchReport('by-country', baseFilters),
        PREFETCH_OPTIONS,
      ),
      queryClient.prefetchQuery(
        ['reports', 'by-city', baseFilters],
        () => fetchReport('by-city', baseFilters),
        PREFETCH_OPTIONS,
      ),
    )
  }

  return Promise.allSettled(tasks)
}
